import { getSelectedProduktlinie } from '@ps20-frontend/preis-lib';
import { Produktlinie, UebergreifendItem, ZusatzoptionItem } from '@ps20-frontend/frontend-models';
import { isNeugeschaeft } from '@ps20-frontend/shop-integration-redux';
import { TaasTarifrisikoschluesselTKV_V2 } from '@ps20/bff-models';
import { IAppState } from '../appState';
import { CoverageType } from '../models/vertragsoptionen.model';
import {
  selectIsTkvV2x,
  selectHasWechselbaustein,
  selectHasPreinsuredWechselbaustein
} from './vertragsoptionen.selector';
import { selectUseExistingWechselbaustein } from './versicherungsnehmer.selector';

const allowedProduktlinien: Record<string, string[]> = {
  'OP-Schutz Basis': ['OP-Schutz Basis'],
  'OP-Schutz Smart': ['OP-Schutz Basis', 'OP-Schutz Smart'],
  'OP-Schutz Komfort': ['OP-Schutz Basis', 'OP-Schutz Smart', 'OP-Schutz Komfort'],
  'OP-Schutz Premium': [
    'OP-Schutz Basis',
    'OP-Schutz Smart',
    'OP-Schutz Komfort',
    'OP-Schutz Premium',
  ],
  'Vollschutz Basis': [
    'OP-Schutz Basis',
    'OP-Schutz Smart',
    'OP-Schutz Komfort',
    'OP-Schutz Premium',
    'Vollschutz Basis',
  ],
  'Vollschutz Smart': [
    'OP-Schutz Basis',
    'OP-Schutz Smart',
    'OP-Schutz Komfort',
    'OP-Schutz Premium',
    'Vollschutz Basis',
    'Vollschutz Smart',
  ],
  'Vollschutz Komfort': [
    'OP-Schutz Basis',
    'OP-Schutz Smart',
    'OP-Schutz Komfort',
    'OP-Schutz Premium',
    'Vollschutz Basis',
    'Vollschutz Smart',
    'Vollschutz Komfort',
  ],
  'Vollschutz Premium': [
    'OP-Schutz Basis',
    'OP-Schutz Smart',
    'OP-Schutz Komfort',
    'OP-Schutz Premium',
    'Vollschutz Basis',
    'Vollschutz Smart',
    'Vollschutz Komfort',
    'Vollschutz Premium',
  ],
};

export const selectSelectedProduktlinie = (state: IAppState): Produktlinie =>
  getSelectedProduktlinie(state.produkt);

export const selectShouldShowZusatzoptionTarifwechsel = (state: IAppState): boolean => {
  if (isNeugeschaeft(state.vertragsoptionen)) {
    return true;
  }
  if (selectIsTkvV2x(state)) {
    return (
      selectZusatzoptionItems(state)?.some(
        zusatzOption => zusatzOption.name === TaasTarifrisikoschluesselTKV_V2.TW,
      ) && selectHasPreinsuredWechselbaustein(state)
    );
  }
  return true;
};
export const selectZusatzoptionItems = (state: IAppState): Array<ZusatzoptionItem> => {
  const produktlinie = getSelectedProduktlinie(state.produkt);
  return produktlinie?.zusatzoptionen || [];
};

export const selectProduktlinien = (state: IAppState): Array<Produktlinie> =>
  state.produkt?.produktlinien || [];

export const selectUebergreifend = (state: IAppState): Array<UebergreifendItem> =>
  state.produkt?.uebergreifend || [];

export const selectSelectedZusatzoptionItems = (state: IAppState): Array<ZusatzoptionItem> => {
  return selectZusatzoptionItems(state).filter(elem => elem.isSelected);
};

export const selectTarifWechselOptionSelected = (state: IAppState): boolean => {
  return selectSelectedZusatzoptionItems(state).some(elem => elem.name === TaasTarifrisikoschluesselTKV_V2.TW);
};

export const selectSelectedProduktlinieIndex = (state: IAppState): number => {
  return state.produkt.produktlinien.findIndex(produktlinie => produktlinie.selected);
};

export const selectGesamtbeitrag = (state: IAppState): number => state.gesamtbeitrag;

export const selectIsAnyProduktlinieDeactivated = (state: IAppState): boolean => {
  return state.produkt.produktlinien.some(pl => pl.deactivated);
};

export const selectSelectedInVorvertragProduktlinieIndex = (state: IAppState): number => {
  const verkaufsangebotKurzname = state.vertragsoptionen.vorvertragsNummern?.[0].verkaufsangebotKurzname;
  if (!verkaufsangebotKurzname) {
    return -1;
  }
  return state.produkt.produktlinien.findIndex(produktlinie => produktlinie.kurztext === verkaufsangebotKurzname);
};

export function selectPossibleProduktlinien(
  produktlinien: Array<Produktlinie>,
  state: IAppState,
  wantsCoverageUpgrade: boolean,
): Array<Produktlinie> {
  const feVorvertragsnummer = state.vertragsoptionen.vorvertragsNummern?.[0];
  const vorvertragAngebotKurzname =
    feVorvertragsnummer?.verkaufsangebotKurzname;
  const preisverzeichnis = feVorvertragsnummer?.vorbelegungForBeibehaltung?.preisverzeichnis;
  const isTkv20 = preisverzeichnis && preisverzeichnis < 100;
  if (wantsCoverageUpgrade || !vorvertragAngebotKurzname || isTkv20) {
    return produktlinien.map(pl => ({ ...pl, deactivated: false }));
  }

  const tarifBezeichnungVorvertrag = produktlinien.find(
    produktlinie => produktlinie.kurztext === vorvertragAngebotKurzname,
  )?.tarifBezeichnung;

  if (!tarifBezeichnungVorvertrag && state.vertragsoptionen.coverageType === CoverageType.OPSCHUTZ) {
    return produktlinien.map(pl => ({ ...pl, deactivated: false }));
  }

  const allowedTarifbezeichnungen = allowedProduktlinien[tarifBezeichnungVorvertrag];

  // Check if vorvertrag matches any produktlinie
  const hasMatchingProduktlinie = produktlinien.some(
    pl => pl.kurztext === vorvertragAngebotKurzname,
  );

  return produktlinien.map(pl => {
    const isSelected = pl.kurztext === vorvertragAngebotKurzname;
    const deactivated = allowedTarifbezeichnungen ? !allowedTarifbezeichnungen.includes(pl.tarifBezeichnung) : true;
    return {
      ...pl,
      // Only reset selection if we found a matching produktlinie
      selected: hasMatchingProduktlinie ? isSelected : pl.selected,
      deactivated,
    };
  });
}

export const selectShouldDisplayBadgeInclusive = (state: IAppState): boolean => {
  return (
    selectHasWechselbaustein(state) &&
    selectUseExistingWechselbaustein(state) === true
  );
};

